import React, { useEffect, useMemo, useState } from "react";
import { X, Save } from "lucide-react";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import { fetchCompanyCOA } from "../Accounting/FixedAssets/coaFetch";
import "../Accounting/FixedAssets/fixedAssets.css";

export default function InventoryItemFormModal({ item, onClose, onSaved }) {
  const { companyId } = useCompany();
  const isEdit = Boolean(item?.id);

  const [coa, setCoa] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const [form, setForm] = useState({
    sku: item?.sku || "",
    name: item?.name || "",
    item_type: item?.item_type || "stock",
    unit: item?.unit || "pcs",
    is_active: item?.is_active ?? true,
    buffer_stock: String(item?.buffer_stock ?? 0),
    reorder_level: String(item?.reorder_level ?? 0),

    // ✅ NEW: selling price
    sales_price: String(item?.sales_price ?? 0),

    inventory_account_id: item?.inventory_account_id || "",
    cogs_account_id: item?.cogs_account_id || "",
    adjustment_account_id: item?.adjustment_account_id || "",
  });

  useEffect(() => {
    (async () => {
      if (!companyId) return;
      const data = await fetchCompanyCOA(companyId);
      setCoa(data || []);
    })();
  }, [companyId]);

  const assetAccounts = useMemo(
    () => coa.filter((a) => a.type === "Asset"),
    [coa]
  );
  const cosAccounts = useMemo(
    () => coa.filter((a) => a.type === "Cost of Sales"),
    [coa]
  );
  const adjAccounts = useMemo(
    () => coa.filter((a) => a.type === "Expense" || a.type === "Revenue"),
    [coa]
  );

  function setField(k, v) {
    setForm((p) => ({ ...p, [k]: v }));
  }

  async function save() {
    setErr("");
    if (!companyId) return;

    if (!form.sku.trim() || !form.name.trim()) {
      setErr("SKU and Name are required.");
      return;
    }

    // ✅ NEW: validate sales_price
    const sp = Number(
      String(form.sales_price ?? "")
        .replace(/,/g, "")
        .trim()
    );
    if (!Number.isFinite(sp) || sp < 0) {
      setErr("Sales price must be a valid number (>= 0).");
      return;
    }

    if (form.item_type === "stock") {
      if (
        !form.inventory_account_id ||
        !form.cogs_account_id ||
        !form.adjustment_account_id
      ) {
        setErr(
          "For stock items, set Inventory, COGS, and Adjustment accounts."
        );
        return;
      }
    }

    setBusy(true);
    try {
      const payload = {
        company_id: companyId,
        sku: form.sku.trim(),
        name: form.name.trim(),
        item_type: form.item_type,
        unit: form.unit.trim() || "pcs",
        is_active: Boolean(form.is_active),
        buffer_stock: Number(form.buffer_stock || 0),
        reorder_level: Number(form.reorder_level || 0),

        // ✅ NEW: persist sales_price
        sales_price: sp,

        inventory_account_id:
          form.item_type === "stock" ? form.inventory_account_id : null,
        cogs_account_id:
          form.item_type === "stock" ? form.cogs_account_id : null,
        adjustment_account_id:
          form.item_type === "stock" ? form.adjustment_account_id : null,
      };

      if (isEdit) {
        const { error } = await supabase
          .from("inventory_items")
          .update(payload)
          .eq("company_id", companyId)
          .eq("id", item.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("inventory_items")
          .insert(payload);
        if (error) throw error;
      }

      onSaved?.();
    } catch (e) {
      setErr(e.message || "Failed to save.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.97)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 14,
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div
        className="fa-panel"
        style={{ width: "min(860px, 100%)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{ display: "flex", justifyContent: "space-between", gap: 10 }}
        >
          <div>
            <div style={{ fontSize: 18, fontWeight: 900 }}>
              {isEdit ? "Edit Item" : "Add Item"}
            </div>
            <div style={{ opacity: 0.8, fontSize: 13, marginTop: 4 }}>
              Set buffer/reorder, selling price, and journal mappings.
            </div>
          </div>
          <button className="fa-icon-btn" onClick={onClose} title="Close">
            <X size={16} />
          </button>
        </div>

        <div className="fa-toolbar" style={{ marginTop: 12 }}>
          <input
            className="fa-input"
            placeholder="SKU"
            value={form.sku}
            onChange={(e) => setField("sku", e.target.value)}
            disabled={busy}
          />
          <input
            className="fa-input"
            placeholder="Item name"
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            disabled={busy}
          />
          <select
            className="fa-select"
            value={form.item_type}
            onChange={(e) => setField("item_type", e.target.value)}
            disabled={busy}
          >
            <option value="stock">Stock</option>
            <option value="service">Service</option>
          </select>
          <input
            className="fa-input"
            placeholder="Unit (pcs, kg…)"
            value={form.unit}
            onChange={(e) => setField("unit", e.target.value)}
            disabled={busy}
          />
        </div>

        {/* ✅ NEW: Selling price row (does NOT remove anything) */}
        <div className="fa-toolbar" style={{ marginTop: 10 }}>
          <input
            className="fa-input"
            type="number"
            placeholder="Sales price"
            value={form.sales_price}
            onChange={(e) => setField("sales_price", e.target.value)}
            disabled={busy}
            min="0"
            step="0.01"
          />

          <input
            className="fa-input"
            type="number"
            placeholder="Buffer stock"
            value={form.buffer_stock}
            onChange={(e) => setField("buffer_stock", e.target.value)}
            disabled={busy}
          />
          <input
            className="fa-input"
            type="number"
            placeholder="Reorder level"
            value={form.reorder_level}
            onChange={(e) => setField("reorder_level", e.target.value)}
            disabled={busy}
          />
          <label
            style={{
              display: "flex",
              gap: 10,
              alignItems: "center",
              fontSize: 13,
              opacity: 0.9,
            }}
          >
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setField("is_active", e.target.checked)}
              disabled={busy}
            />
            Active
          </label>
        </div>

        {form.item_type === "stock" && (
          <div className="fa-card" style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 900, marginBottom: 10 }}>
              Accounting Mapping
            </div>

            <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 6 }}>
              Inventory Asset Account
            </div>
            <select
              className="fa-select"
              value={form.inventory_account_id}
              onChange={(e) => setField("inventory_account_id", e.target.value)}
              style={{ width: "100%" }}
              disabled={busy}
            >
              <option value="">Select account…</option>
              {assetAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code ? `${a.code} - ` : ""}
                  {a.name} ({a.category || "—"})
                </option>
              ))}
            </select>

            <div style={{ fontSize: 12, opacity: 0.8, margin: "10px 0 6px" }}>
              COGS Account
            </div>
            <select
              className="fa-select"
              value={form.cogs_account_id}
              onChange={(e) => setField("cogs_account_id", e.target.value)}
              style={{ width: "100%" }}
              disabled={busy}
            >
              <option value="">Select account…</option>
              {cosAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code ? `${a.code} - ` : ""}
                  {a.name} ({a.category || "—"})
                </option>
              ))}
            </select>

            <div style={{ fontSize: 12, opacity: 0.8, margin: "10px 0 6px" }}>
              Adjustment / Clearing Account
            </div>
            <select
              className="fa-select"
              value={form.adjustment_account_id}
              onChange={(e) =>
                setField("adjustment_account_id", e.target.value)
              }
              style={{ width: "100%" }}
              disabled={busy}
            >
              <option value="">Select account…</option>
              {adjAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.code ? `${a.code} - ` : ""}
                  {a.name} ({a.type})
                </option>
              ))}
            </select>
            <div style={{ opacity: 0.75, fontSize: 12, marginTop: 6 }}>
              Used as <b>Inventory clearing</b> for Stock IN (until AP is added)
              and as <b>adjustment</b> account for stock counts.
            </div>
          </div>
        )}

        {err && (
          <div style={{ marginTop: 10, color: "#ffb4b4", fontSize: 13 }}>
            {err}
          </div>
        )}

        <div
          className="fa-actions"
          style={{ marginTop: 12, justifyContent: "flex-end" }}
        >
          <button className="fa-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="fa-btn fa-btn-primary"
            onClick={save}
            disabled={busy}
          >
            <Save size={16} /> {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
